/**
 * Web Push subscription (closed-window wake, a recovery path). The VAPID public key comes from
 * LaunchResponse.push.vapid_public_key, else the build-time env CC_VAPID_PUBLIC_KEY; without a
 * key the subscription step is skipped and the window-stays-open rule carries the shift.
 */
import { z } from 'zod';
import type { ApiClient } from './api.js';
import type { Logger } from '../log.js';

export function urlBase64ToUint8Array(b64: string): Uint8Array {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export async function subscribePush(reg: ServiceWorkerRegistration, api: ApiClient, vapidPublicKey: string | null, log: Logger): Promise<boolean> {
  if (!vapidPublicKey || !('pushManager' in reg)) return false;
  try {
    const existing = await reg.pushManager.getSubscription();
    const sub = existing ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource }));
    const json = sub.toJSON();
    if (!json.endpoint || !json.keys?.['p256dh'] || !json.keys?.['auth']) return false;
    await api.request('POST', '/v1/push/subscribe', z.object({ id: z.string().optional() }), {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys['p256dh'], auth: json.keys['auth'] },
      expirationTime: json.expirationTime ?? null,
    });
    log.info('push subscription stored');
    return true;
  } catch (e) {
    log.warn({ err: (e as Error).message }, 'push subscribe failed');
    return false;
  }
}
